/**
 * PricingCard Component
 *
 * Displays pricing and CTA button
 * Shows loading state during checkout
 */
import type { ReactNode } from 'react';
import { View, Text, Pressable, ActivityIndicator } from "react-native";
import { useTranslation } from "@dramadetector/shared";
import { formatPriceDisplay } from "../../../lib/pricing-i18n";

type BillingInterval = 'week' | 'month' | 'quarter' | 'semester' | 'year';

interface PricingCardProps {
  selectedInterval: BillingInterval;
  price: number; // Changed from displayPrice to numeric price
  isLoading: boolean;
  isAuthPending?: boolean;
  onSubscribe: () => void;
  /** When false, shows disabled "Coming Soon" CTA instead of active checkout button */
  checkoutEnabled?: boolean;
}

export function PricingCard({
  selectedInterval,
  price,
  isLoading,
  isAuthPending = false,
  onSubscribe,
  checkoutEnabled = true,
}: PricingCardProps) {
  const { t } = useTranslation('subscription');

  const displayPrice = formatPriceDisplay(price, selectedInterval, t);
  const isDisabled = isLoading || isAuthPending || !checkoutEnabled;

  function renderButtonContent(): ReactNode {
    if (isLoading) {
      return <ActivityIndicator color="white" />;
    }

    if (isAuthPending) {
      return (
        <Text className="text-gray-600 text-center text-lg font-bold">
          Preparing secure checkout…
        </Text>
      );
    }

    if (!checkoutEnabled) {
      return (
        <Text className="text-gray-500 text-center text-lg font-bold">
          {t('cta.coming_soon', { defaultValue: 'Coming Soon' })}
        </Text>
      );
    }

    return (
      <Text className="text-white text-center text-lg font-bold">
        {t('cta.button', {
          interval: t(`intervals.${selectedInterval}`),
          price: displayPrice
        })}
      </Text>
    );
  }

  return (
    <View className="px-4 pb-8">
      <Pressable
        onPress={onSubscribe}
        disabled={isDisabled}
        testID="checkout-button"
        className={`py-4 rounded-full ${
          isDisabled ? 'bg-gray-300' : 'bg-purple-600 active:bg-purple-700'
        }`}
      >
        {renderButtonContent()}
      </Pressable>

      {/* Terms */}
      <Text className="text-gray-400 text-xs text-center mt-4">
        {t('cta.terms')}
      </Text>
    </View>
  );
}
